import React, { useMemo, useState } from 'react';
import { Button, Space, Input, Form, Row, Col, Tag, Tooltip, Typography, notification } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import CustomSelect from '../../components/CustomSelect';
import NormalFormLabel from '../../components/NormalFormLabel';
import { useMutation, useQuery } from '@tanstack/react-query';
import { checkPermission } from '../../utils/checkPermission';
import { newObjectKeys } from '../../utils/utility';
import { TableCustomV2 } from '../../components/TableCustomV2';
import {
  deletePermissionRequest,
  getListPermissionsGroupRequest,
  getListPermissionsRequest,
} from '../../services/permissionRequest';
import { LIST_STATUS_ROLE } from '../../constants/appSelect';
import ThemTinhNang from './components/themTinhNang';
import SuaTinhNang from './components/suaTinhNang';
import {
  CREATE_PERMISSION,
  DELETE_PERMISSION,
  GET_LIST_PERMISSIONS,
  UPDATE_PERMISSION,
} from '../../constants/apiEndPoint';
import { isEqual } from 'lodash';

const DanhSachTinhNang = () => {
  const [createPermissionOpen, setCreatePermissionOpen] = useState(false);
  const [updatePermissionOpen, setUpdatePermissionOpen] = useState(false);
  const [permissionInfo, setPermissionInfo] = useState(null);
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  const {
    data: dataPermissions,
    isPending,
    refetch: refetchDataPermissions,
  } = useQuery({
    queryKey: ['dataPermissions', params],
    queryFn: () => getListPermissionsRequest(params),
    enabled: checkPermission(GET_LIST_PERMISSIONS, 'Xem danh sách'),
  });

  // get data permission group
  const { data: dataPermissionGroup } = useQuery({
    queryKey: ['dataPermissionGroup'],
    queryFn: () => getListPermissionsGroupRequest(),
  });

  // convert list partners options
  const dataPermissionGroupOption = useMemo(() => {
    const options =
      dataPermissionGroup?.map((item) => {
        return { label: item?.name, value: item?.id };
      }) || [];
    return options;
  }, [dataPermissionGroup]);

  const { mutate: mutateDeletePermission } = useMutation({
    mutationFn: deletePermissionRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Xóa nhóm quyền thành công!',
        });
        refetchDataPermissions();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '80px',
      fixed: 'left',
    },
    {
      title: 'Tên tính năng',
      dataIndex: 'name',
      render: (name) => <Typography.Text className="text-[#1677FF] hover:cursor-pointer">{name}</Typography.Text>,
      key: 'name',
      width: '200px',
      fixed: 'left',
    },
    {
      title: 'Nhóm tính năng',
      dataIndex: 'permission_group_name',
      key: 'permission_group_name',
      width: '200px',
    },
    {
      title: 'Key menu',
      dataIndex: 'key_menu',
      key: 'key_menu',
      width: '120px',
    },
    {
      title: 'URL View',
      dataIndex: 'url',
      key: 'url',
      width: '120px',
    },
    {
      title: 'API',
      dataIndex: 'api',
      key: 'api',
    },
    {
      title: 'Danh sách actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (value) => (
        <Space direction={'vertical'} size={4} split={<hr />} className={'w-full'}>
          {value?.map((item, key) => (
            <div className={'flex gap-2 items-center'} key={key}>
              <div>
                <Tag>{item?.name}</Tag>
              </div>
              <Typography.Text>{item?.api}</Typography.Text>
            </div>
          ))}
        </Space>
      ),
    },

    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          style={{
            backgroundColor: LIST_STATUS_ROLE.find((item) => item.value === status)?.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
          }}
        >
          {LIST_STATUS_ROLE.find((item) => item.value === status)?.label}
        </Tag>
      ),
      width: '100px',
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {checkPermission(UPDATE_PERMISSION, 'Chỉnh sửa') && (
            <Tooltip title="Chỉnh sửa tính năng">
              <EditOutlined
                className="text-base"
                onClick={() => {
                  setPermissionInfo(record);
                  setUpdatePermissionOpen(true);
                }}
              />
            </Tooltip>
          )}
          {checkPermission(DELETE_PERMISSION, 'Xóa') && record?.status !== 2 && (
            <Tooltip title="Xóa tính năng">
              <DeleteOutlined className="text-base" onClick={() => mutateDeletePermission({ id: record?.id })} />
            </Tooltip>
          )}
        </Space>
      ),
      width: '90px',
      fixed: 'right',
    },
  ];

  const handleSubmitForm = (values) => {
    const temp = { pageId: 1, limit: 20 };

    if (values?.name) {
      Object.assign(temp, { name: values?.name });
    }

    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    } else {
      Object.assign(temp, { status: [0, 1] });
    }

    if (isEqual(temp, params)) {
      refetchDataPermissions();
    } else {
      setParams(temp);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="font-semibold text-2xl font-roboto">Danh sách tính năng</h1>
        {checkPermission(CREATE_PERMISSION, 'Tạo mới') && (
          <Button type="primary" onClick={() => setCreatePermissionOpen(true)}>
            <PlusOutlined /> Thêm tính năng
          </Button>
        )}
      </div>

      <ThemTinhNang
        open={createPermissionOpen}
        setOpen={setCreatePermissionOpen}
        dataPermissionGroupOption={dataPermissionGroupOption}
        refetchDataPermissions={refetchDataPermissions}
      />

      <SuaTinhNang
        open={updatePermissionOpen}
        setOpen={setUpdatePermissionOpen}
        dataPermissionGroupOption={dataPermissionGroupOption}
        refetchDataPermissions={refetchDataPermissions}
        permissionInfo={permissionInfo}
        setPermissionInfo={setPermissionInfo}
      />

      {checkPermission(GET_LIST_PERMISSIONS, 'Xem danh sách') && (
        <>
          <Form layout="vertical" name="search-account" form={formSearch} onFinish={handleSubmitForm}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Tên tính năng" />} name="name">
                  <Input allowClear placeholder="Nhập tên nhóm quyền..." />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Trạng thái" />} name="status" initialValue={-1}>
                  <CustomSelect
                    placeholder="Trạng thái"
                    options={[{ value: -1, label: 'Tất cả' }, ...LIST_STATUS_ROLE]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6} className="flex items-end">
                <Form.Item>
                  <Button type="primary" className=" " htmlType="submit">
                    <SearchOutlined /> Tìm kiếm
                  </Button>
                  {params &&
                    Object.values(newObjectKeys(params, ['name', 'status'])).some(
                      (value) => (value && !Array.isArray(value)) || value?.length === 1
                    ) && (
                      <Button
                        htmlType="button"
                        className="ml-3"
                        onClick={() => {
                          formSearch.resetFields();
                          setParams({ pageId: 1, limit: params.limit });
                        }}
                      >
                        <DeleteOutlined />
                        Xóa lọc
                      </Button>
                    )}
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <TableCustomV2
            isLoading={isPending}
            columns={columns}
            dataSource={dataPermissions?.data}
            totalItem={dataPermissions?.totalItem}
            params={params}
            setParams={setParams}
          />
        </>
      )}
    </div>
  );
};

export default DanhSachTinhNang;
